import { motion } from "framer-motion";

/**
 * MeditationAura - A hypnotic rotating vortex for the meditation screen
 * Deep indigo and violet portal with particles being sucked into the center
 * Creates a trance-inducing, inward-pulling visual effect
 */
export const MeditationAura = ({ className = "" }) => {
    return (
        <div className={`relative w-72 h-72 sm:w-96 sm:h-96 ${className}`}>
            {/* Outer rotating ring - slowest */}
            <motion.div
                className="absolute inset-0 rounded-full"
                style={{
                    background: "conic-gradient(from 0deg, rgba(75,0,130,0.3), rgba(138,43,226,0.2), rgba(48,25,91,0.3), rgba(75,0,130,0.3))",
                    filter: "blur(8px)",
                }}
                animate={{
                    rotate: 360,
                    scale: [1, 1.08, 1],
                }}
                transition={{
                    rotate: {
                        duration: 30,
                        repeat: Infinity,
                        ease: "linear",
                    },
                    scale: {
                        duration: 6,
                        repeat: Infinity,
                        ease: "easeInOut",
                    },
                }}
            />

            {/* Second ring - opposite rotation */}
            <motion.div
                className="absolute inset-[8%] rounded-full"
                style={{
                    background: "conic-gradient(from 180deg, rgba(138,43,226,0.35), rgba(75,0,130,0.25), rgba(147,112,219,0.3), rgba(138,43,226,0.35))",
                    filter: "blur(6px)",
                }}
                animate={{
                    rotate: -360,
                    scale: [1, 1.1, 1],
                }}
                transition={{
                    rotate: {
                        duration: 25,
                        repeat: Infinity,
                        ease: "linear",
                    },
                    scale: {
                        duration: 6,
                        repeat: Infinity,
                        ease: "easeInOut",
                        delay: 0.5,
                    },
                }}
            />

            {/* Third ring - deep violet glow */}
            <motion.div
                className="absolute inset-[18%] rounded-full"
                style={{
                    background: "conic-gradient(from 90deg, rgba(147,112,219,0.4), rgba(138,43,226,0.3), rgba(75,0,130,0.4), rgba(147,112,219,0.4))",
                    filter: "blur(4px)",
                }}
                animate={{
                    rotate: 360,
                    scale: [1, 1.12, 1],
                }}
                transition={{
                    rotate: {
                        duration: 20,
                        repeat: Infinity,
                        ease: "linear",
                    },
                    scale: {
                        duration: 6,
                        repeat: Infinity,
                        ease: "easeInOut",
                        delay: 1,
                    },
                }}
            />

            {/* Fourth ring - faster rotation */}
            <motion.div
                className="absolute inset-[28%] rounded-full"
                style={{
                    background: "conic-gradient(from 270deg, rgba(138,43,226,0.5), rgba(147,112,219,0.4), rgba(186,85,211,0.45), rgba(138,43,226,0.5))",
                    filter: "blur(3px)",
                }}
                animate={{
                    rotate: -360,
                    scale: [1, 1.08, 1],
                }}
                transition={{
                    rotate: {
                        duration: 15,
                        repeat: Infinity,
                        ease: "linear",
                    },
                    scale: {
                        duration: 6,
                        repeat: Infinity,
                        ease: "easeInOut",
                        delay: 1.5,
                    },
                }}
            />

            {/* Inner vortex core */}
            <motion.div
                className="absolute inset-[38%] rounded-full"
                style={{
                    background: "radial-gradient(circle, rgba(216,191,216,0.6) 0%, rgba(186,85,211,0.4) 30%, rgba(138,43,226,0.5) 60%, transparent 80%)",
                    boxShadow: "0 0 60px rgba(138,43,226,0.5), 0 0 100px rgba(147,112,219,0.3), inset 0 0 40px rgba(216,191,216,0.3)",
                }}
                animate={{
                    rotate: 360,
                    scale: [0.95, 1.05, 0.95],
                    opacity: [0.7, 1, 0.7],
                }}
                transition={{
                    rotate: {
                        duration: 10,
                        repeat: Infinity,
                        ease: "linear",
                    },
                    scale: {
                        duration: 6,
                        repeat: Infinity,
                        ease: "easeInOut",
                    },
                    opacity: {
                        duration: 6,
                        repeat: Infinity,
                        ease: "easeInOut",
                    },
                }}
            />

            {/* Central bright point - the eye of the vortex */}
            <motion.div
                className="absolute inset-[44%] rounded-full"
                style={{
                    background: "radial-gradient(circle, rgba(255,255,255,0.9) 0%, rgba(216,191,216,0.7) 30%, rgba(186,85,211,0.4) 60%, transparent 80%)",
                    boxShadow: "0 0 30px rgba(255,255,255,0.5), 0 0 60px rgba(216,191,216,0.4)",
                }}
                animate={{
                    scale: [0.8, 1.1, 0.8],
                    opacity: [0.6, 1, 0.6],
                }}
                transition={{
                    duration: 6,
                    repeat: Infinity,
                    ease: "easeInOut",
                }}
            />

            {/* Particles being sucked into the center */}
            {[...Array(16)].map((_, i) => {
                const angle = (i * 360) / 16;
                const startRadius = 45 + (i % 3) * 5;
                const delay = (i * 0.25) % 4;
                const duration = 3 + (i % 2);
                
                return (
                    <motion.div
                        key={`particle-${i}`}
                        className="absolute rounded-full"
                        style={{
                            width: 3 + (i % 3),
                            height: 3 + (i % 3),
                            background: `radial-gradient(circle, rgba(216,191,216,${0.6 + (i % 3) * 0.1}) 0%, rgba(138,43,226,0.4) 60%, transparent 100%)`,
                            left: "50%",
                            top: "50%",
                            boxShadow: `0 0 ${4 + (i % 3) * 2}px rgba(186,85,211,0.5)`,
                        }}
                        animate={{
                            x: [
                                `${Math.cos((angle * Math.PI) / 180) * startRadius}%`,
                                `${Math.cos((angle * Math.PI) / 180) * 5}%`,
                            ],
                            y: [
                                `${Math.sin((angle * Math.PI) / 180) * startRadius}%`,
                                `${Math.sin((angle * Math.PI) / 180) * 5}%`,
                            ],
                            opacity: [0, 0.8, 0.6, 0],
                            scale: [0.5, 1, 0.8, 0.3],
                        }}
                        transition={{
                            duration: duration,
                            repeat: Infinity,
                            delay: delay,
                            ease: "easeIn",
                        }}
                    />
                );
            })}

            {/* Outer ambient particles */}
            {[...Array(8)].map((_, i) => {
                const angle = (i * 360) / 8 + 22.5;
                const startRadius = 55;
                
                return (
                    <motion.div
                        key={`outer-particle-${i}`}
                        className="absolute rounded-full"
                        style={{
                            width: 2,
                            height: 2,
                            background: "rgba(186,85,211,0.6)",
                            left: "50%",
                            top: "50%",
                            boxShadow: "0 0 4px rgba(186,85,211,0.8)",
                        }}
                        animate={{
                            x: [
                                `${Math.cos((angle * Math.PI) / 180) * startRadius}%`,
                                `${Math.cos((angle * Math.PI) / 180) * 10}%`,
                            ],
                            y: [
                                `${Math.sin((angle * Math.PI) / 180) * startRadius}%`,
                                `${Math.sin((angle * Math.PI) / 180) * 10}%`,
                            ],
                            opacity: [0, 0.5, 0.3, 0],
                            scale: [0.3, 0.8, 0.5, 0.1],
                        }}
                        transition={{
                            duration: 5,
                            repeat: Infinity,
                            delay: i * 0.6,
                            ease: "easeIn",
                        }}
                    />
                );
            })}

            {/* Spiral arms suggestion */}
            {[0, 120, 240].map((rotation, i) => (
                <motion.div
                    key={`spiral-${i}`}
                    className="absolute inset-[15%] rounded-full"
                    style={{
                        background: `conic-gradient(from ${rotation}deg, transparent 0%, rgba(138,43,226,0.15) 15%, transparent 30%)`,
                    }}
                    animate={{
                        rotate: [rotation, rotation + 360],
                        opacity: [0.3, 0.6, 0.3],
                    }}
                    transition={{
                        rotate: {
                            duration: 20,
                            repeat: Infinity,
                            ease: "linear",
                        },
                        opacity: {
                            duration: 6,
                            repeat: Infinity,
                            ease: "easeInOut",
                            delay: i * 2,
                        },
                    }}
                />
            ))}

            {/* Outer breathing ring */}
            <motion.div
                className="absolute inset-[-5%] rounded-full border border-purple-500/10"
                animate={{
                    scale: [1, 1.03, 1],
                    opacity: [0.1, 0.2, 0.1],
                }}
                transition={{
                    duration: 6,
                    repeat: Infinity,
                    ease: "easeInOut",
                }}
            />
        </div>
    );
};

export default MeditationAura;
